import type { SyncEntity, SyncOp } from "@coach/shared";
import type { LocalDb, SqlValue } from "@coach/sync";
import { enqueue } from "@coach/sync";

/**
 * Repository helpers shared by every feature.
 *
 * Pattern:
 *   await withLocalWrite(db, async (tx) => {
 *     await tx.exec('INSERT INTO coach (...) VALUES (...)', [...]);
 *     await enqueueOp(tx, { entity: 'coach', kind: 'upsert', payload, updatedAt, baseRev });
 *   });
 *
 * `withLocalWrite` runs everything in a single SQLite transaction so a crash
 * never leaves a domain row without its outbox companion.
 */

export async function withLocalWrite<T>(db: LocalDb, fn: (tx: LocalDb) => Promise<T>): Promise<T> {
  return db.transaction(fn);
}

export interface EnqueueArgs {
  entity: SyncEntity;
  kind: SyncOp["kind"];
  payload: Record<string, unknown>;
  updatedAt: string;
  baseRev: number | null;
}

export async function enqueueOp(tx: LocalDb, args: EnqueueArgs): Promise<void> {
  await enqueue(tx, {
    opId: crypto.randomUUID(),
    entity: args.entity,
    kind: args.kind,
    payload: args.payload,
    updatedAt: args.updatedAt,
    baseRev: args.baseRev,
  });
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function newId(): string {
  return crypto.randomUUID();
}

/** Type-safe row reader: any column may be null in SQLite. */
export async function selectAll<T>(
  db: LocalDb,
  sql: string,
  params: SqlValue[] = [],
): Promise<T[]> {
  return db.query<Record<string, SqlValue>>(sql, params) as unknown as Promise<T[]>;
}
